import { AtgChip } from "@/components/AtgChip";
import { PlayerName } from "@/components/PlayerCardDialog";
import { ArchetypeBadge } from "@/components/ArchetypeBadge";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Loader2, Search, ChevronDown, Users, Activity } from "lucide-react";
import GoalieCheatSheet from "@/components/GoalieCheatSheet";
import ArchetypePerformance from "@/components/ArchetypePerformance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { key: "all", label: "ALL SLATE" },
  { key: "game", label: "GAME PAIRS" },
  { key: "slate", label: "SLATE PAIRS" },
  { key: "contrarian", label: "CONTRARIAN" },
  { key: "pp", label: "PP PAIRS" },
  { key: "line", label: "LINE PAIRS" },
  { key: "cross", label: "CROSS-GAME" },
  { key: "archetype", label: "ARCHETYPE" },
];

const SORTS = [
  { key: "pair_score", label: "Pair Score" },
  { key: "correlation", label: "Correlation" },
  { key: "cross", label: "Cross-Game" },
  { key: "same", label: "Same-Game" },
];

const BAND_STYLE = {
  "ELITE PAIR": "border-fuchsia-600/60 bg-fuchsia-950/30 text-fuchsia-300",
  "CORE PAIR": "border-rose-700/60 bg-rose-950/30 text-rose-300",
  "VALUE PAIR": "border-cyan-700/60 bg-cyan-950/30 text-cyan-300",
  "CONTRARIAN PAIR": "border-amber-700/60 bg-amber-950/30 text-amber-300",
  "WATCH": "border-slate-600/60 bg-slate-800/40 text-slate-300",
};

const StatCell = ({ label, value }) => (
  <div className="flex flex-col items-center rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2">
    <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{label}</span>
    <span className="font-display text-lg font-bold text-cyan-300">{value}</span>
  </div>
);

// Final selection layer — diversify without touching raw scores (spec section 9).
// Max 2 appearances/player, max 3/team, max 2/game (game cap relaxed for single-game views).
function diversifyPairs(pairs, { relaxGame = false, limit = 10 } = {}) {
  const perPlayer = {}, perTeam = {}, perGame = {};
  const picked = [];
  for (const p of pairs) {
    const a = p.player_a, b = p.player_b;
    const pa = `${a.team}${a.jersey}`, pb = `${b.team}${b.jersey}`;
    const games = new Set([a.game_label, b.game_label]);
    if ((perPlayer[pa] || 0) >= 2 || (perPlayer[pb] || 0) >= 2) continue;
    if ((perTeam[a.team] || 0) >= 3 || (perTeam[b.team] || 0) >= 3) continue;
    if (!relaxGame && [...games].some((g) => (perGame[g] || 0) >= 2)) continue;
    picked.push(p);
    perPlayer[pa] = (perPlayer[pa] || 0) + 1;
    perPlayer[pb] = (perPlayer[pb] || 0) + 1;
    perTeam[a.team] = (perTeam[a.team] || 0) + 1;
    perTeam[b.team] = (perTeam[b.team] || 0) + 1;
    games.forEach((g) => { perGame[g] = (perGame[g] || 0) + 1; });
    if (picked.length >= limit) break;
  }
  return picked;
}

const PlayerBlock = ({ p }) => (
  <div className="flex-1 rounded-lg border border-slate-800 bg-slate-900/40 p-2.5">
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-[12px] font-bold text-cyan-400">{p.jersey != null ? `#${p.jersey}` : "#—"}</span>
      <PlayerName name={p.name} team={p.team} className="font-display text-[13px] font-bold uppercase tracking-tight text-slate-100" />
      <span className="rounded bg-slate-800 px-1 font-mono text-[9px] font-semibold text-slate-300">{p.team}</span>
    </div>
    <div className="mt-1 flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-slate-400">
      <span className="rounded bg-cyan-950/60 px-1 text-cyan-300">{p.slot}</span>
      {p.pp_unit && <span className="rounded bg-amber-950/60 px-1 text-amber-300">{p.pp_unit}</span>}
      <ArchetypeBadge name={p.archetype} testId={`pair-arch-${p.team}-${p.jersey}`} />
      <AtgChip atg={p.atg} testId={`pair-atg-${p.team}-${p.jersey}`} />
    </div>
    <div className="mt-1 font-mono text-[10px] leading-relaxed text-slate-400">
      {p.goalie_name && <span className="text-lime-500/80">{String(p.goalie_name).split(" ").slice(-1)[0]}</span>} origin:{" "}
      <span className={p.origin_goals > 0 ? "font-bold text-lime-300" : "text-slate-600"}>{p.origin_goals}G / {p.origin_games}GM</span>
    </div>
  </div>
);

const PairCard = ({ pair }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/30" data-testid={`pair-card-${pair.pair_id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition hover:bg-slate-900/50"
        data-testid={`pair-toggle-${pair.pair_id}`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className={`shrink-0 rounded-lg px-2 py-1 font-mono text-sm font-bold tabular-nums ${pair.pair_score >= 80 ? "text-rose-300" : "text-cyan-300"}`}>
            {pair.pair_score}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-display text-[13px] font-bold uppercase tracking-tight text-slate-100">
              <PlayerName name={pair.player_a.name} team={pair.player_a.team} className="truncate" />
              <span className="text-slate-600">+</span>
              <PlayerName name={pair.player_b.name} team={pair.player_b.team} className="truncate" />
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">
              <span>{pair.status_emoji} {pair.status}</span>
              <span className="text-slate-700">·</span>
              <span>{pair.primary_type}</span>
              <span className="text-slate-700">·</span>
              <span>{pair.game_label}</span>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase ${BAND_STYLE[pair.band] || "border-slate-700 text-slate-400"}`}>
            {pair.band_emoji} {pair.band}
          </span>
          <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-800 p-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <PlayerBlock p={pair.player_a} />
            <div className="flex items-center justify-center px-1 font-display text-lg font-bold text-slate-600">+</div>
            <PlayerBlock p={pair.player_b} />
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {pair.relationship.map((r) => (
              <span key={r} className="rounded bg-slate-800/70 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-slate-300">{r.replace(/_/g, " ")}</span>
            ))}
            {pair.tags.map((t) => (
              <span key={t} className="rounded bg-cyan-950/60 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-cyan-300">{t.replace(/_/g, " ")}</span>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 font-mono text-[9px] uppercase tracking-wider" data-testid={`pair-signals-${pair.pair_id}`}>
            {Object.entries(pair.signals || {}).map(([k, v]) => (
              <span key={k} className={v ? "text-lime-400" : "text-slate-600"}>{v ? "✓" : "○"} {k.replace(/_/g, " ")}</span>
            ))}
            <span className="text-slate-500">ARCH FIT {pair.archetype_fit}</span>
            <span className="text-slate-500">CORR {pair.correlation}</span>
            <span className="text-slate-500">DATA {pair.data_confidence ?? pair.data_quality}</span>
          </div>
          <div className="mt-2.5 rounded-lg border border-slate-800 bg-slate-950/40 p-2.5">
            <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-lime-500">Why it matches</p>
            <ul className="space-y-1">
              {pair.why.map((w, i) => (
                <li key={i} className="flex gap-1.5 font-mono text-[10px] leading-relaxed text-slate-300">
                  <span className="text-cyan-500">›</span> {w}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const Top10 = ({ pairs }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-800" data-testid="pairing-top10">
    <div className="bg-gradient-to-r from-rose-950/30 to-slate-900/30 px-3 py-2 font-display text-[13px] font-bold uppercase tracking-wide text-rose-300">
      🔥 Top 10 Pairs — Entire Slate
    </div>
    <table className="w-full min-w-[560px] border-collapse">
      <thead>
        <tr className="bg-slate-900/80 font-mono text-[9px] uppercase tracking-wider text-slate-500">
          <th className="px-2 py-1.5 text-left">#</th>
          <th className="px-2 py-1.5 text-left">Player A</th>
          <th className="px-2 py-1.5 text-left">Player B</th>
          <th className="px-2 py-1.5 text-left">Games</th>
          <th className="px-2 py-1.5 text-left">Type</th>
          <th className="px-2 py-1.5 text-right">Score</th>
        </tr>
      </thead>
      <tbody>
        {pairs.map((p, i) => (
          <tr key={p.pair_id} className={`border-t border-slate-800/60 ${i === 0 ? "bg-rose-950/15" : ""}`} data-testid={`top10-row-${i}`}>
            <td className="px-2 py-1.5 font-mono text-[12px] font-bold text-cyan-400">{i + 1}</td>
            <td className="px-2 py-1.5">
              <PlayerName name={p.player_a.name} team={p.player_a.team} className="font-display text-[12px] font-semibold uppercase text-slate-100" />
              <span className="ml-1 font-mono text-[9px] text-slate-500">{p.player_a.team}</span>
            </td>
            <td className="px-2 py-1.5">
              <PlayerName name={p.player_b.name} team={p.player_b.team} className="font-display text-[12px] font-semibold uppercase text-slate-100" />
              <span className="ml-1 font-mono text-[9px] text-slate-500">{p.player_b.team}</span>
            </td>
            <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400">{p.game_label}</td>
            <td className="px-2 py-1.5 font-mono text-[10px] text-cyan-300">{p.primary_type}</td>
            <td className="px-2 py-1.5 text-right font-mono text-[13px] font-bold text-cyan-200 tabular-nums">{p.pair_score}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PairingTab({ apiDate }) {
  const [subTab, setSubTab] = useState("pairs");
  const [poolOpen, setPoolOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState("all");
  const [gameF, setGameF] = useState("ALL");
  const [teamF, setTeamF] = useState("ALL");
  const [playerQ, setPlayerQ] = useState("");
  const [sort, setSort] = useState("pair_score");

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = apiDate ? { date: apiDate } : {};
      const res = await axios.get(`${API}/pairing`, { params, timeout: 240000 });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [apiDate]);

  useEffect(() => { if (subTab === "pairs") load(); }, [load, subTab]);

  const filtered = useMemo(() => {
    if (!data?.pairs) return [];
    let list = [...data.pairs];
    if (mode === "game") list = list.filter((p) => p.same_game);
    else if (mode === "slate") list = list;
    else if (mode === "contrarian") list = list.filter((p) => p.tags.includes("CONTRARIAN") || p.tags.includes("STAR_CONTRARIAN"));
    else if (mode === "pp") list = list.filter((p) => p.tags.includes("PP"));
    else if (mode === "line") list = list.filter((p) => p.relationship.includes("SAME_LINE"));
    else if (mode === "cross") list = list.filter((p) => p.cross_game);
    else if (mode === "archetype") list = list.filter((p) => p.archetype_fit >= 70);

    if (gameF !== "ALL") list = list.filter((p) => p.player_a.game_label === gameF || p.player_b.game_label === gameF);
    if (teamF !== "ALL") list = list.filter((p) => p.player_a.team === teamF || p.player_b.team === teamF);
    if (playerQ.trim()) {
      const q = playerQ.trim().toLowerCase();
      list = list.filter((p) => p.player_a.name.toLowerCase().includes(q) || p.player_b.name.toLowerCase().includes(q));
    }
    if (sort === "correlation") list.sort((a, b) => b.correlation - a.correlation || b.pair_score - a.pair_score);
    else if (sort === "cross") list.sort((a, b) => (b.cross_game - a.cross_game) || b.pair_score - a.pair_score);
    else if (sort === "same") list.sort((a, b) => (b.same_game - a.same_game) || b.pair_score - a.pair_score);
    else list.sort((a, b) => b.pair_score - a.pair_score);
    return list;
  }, [data, mode, gameF, teamF, playerQ, sort]);

  // Main board shows ONLY a diversified Top 10 (spec) — never hundreds of cards.
  const displayed = useMemo(() => {
    if (mode === "all") return data?.top10 || [];
    const singleGame = mode === "game" || gameF !== "ALL";
    return diversifyPairs(filtered, { relaxGame: singleGame, limit: 10 });
  }, [filtered, data, mode, gameF]);

  const SubBar = (
    <div className="flex gap-1.5" data-testid="pairing-subtabs">
      {[["pairs", "🤝 PAIRS"], ["cheat", "🥅 CHEAT SHEET"]].map(([k, l]) => (
        <button
          key={k}
          data-testid={`pairing-subtab-${k}`}
          onClick={() => setSubTab(k)}
          className={`rounded-lg px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition ${
            subTab === k ? "bg-cyan-500/15 text-cyan-300 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.45)]" : "border border-slate-800 text-slate-500 hover:text-slate-300"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  if (subTab === "cheat") {
    return (
      <div className="space-y-4" data-testid="pairing-dashboard">
        {SubBar}
        <GoalieCheatSheet apiDate={apiDate} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4" data-testid="pairing-dashboard">
        {SubBar}
        <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="pairing-loading">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
          <p className="mt-4 font-mono text-xs uppercase tracking-widest">Scanning the full slate…</p>
          <p className="mt-1 font-mono text-[10px] text-slate-600">Building goal-origin pool across every game (first run may take up to a minute)</p>
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="space-y-4" data-testid="pairing-dashboard">
        {SubBar}
        <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="pairing-error">
          <p className="font-mono text-xs uppercase tracking-widest text-rose-400">Pairing engine unavailable</p>
          <button onClick={load} className="mt-3 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pairing-dashboard">
      {SubBar}
      {/* header */}
      <div className="rounded-xl border border-cyan-900/40 bg-gradient-to-r from-cyan-950/20 to-slate-900/20 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-wide text-cyan-300">
              🤝 Cypher Pairing Engine
            </h2>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-lime-400">{data.status}</p>
            <p className="mt-0.5 font-mono text-[10px] text-amber-300/80" data-testid="pairing-analytical-note">Analytical view (legacy CYPHER pair scoring) · official picks come only from the Official NHL Pair Board</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatCell label="Slate" value={`${data.slate_size} GM`} />
            <StatCell label="Teams" value={data.team_count} />
            <StatCell label="Players" value={data.players_analyzed} />
            <StatCell label="Qualified Pairs" value={data.qualified_pairs ?? data.pairs_generated} />
            <StatCell label="Top 10 Shown" value={data.top10_count ?? (data.top10?.length ?? 10)} />
          </div>
        </div>
      </div>

      {/* 🧬 Archetype Performance (APPROVED / WATCH / CAPTURE + live audit) */}
      <ArchetypePerformance perf={data.archetype_performance} onRefresh={load} />

      {/* mode selector */}
      <div className="flex flex-wrap gap-1.5" data-testid="pairing-modes">
        {MODES.map((m) => (
          <button
            key={m.key}
            data-testid={`pair-mode-${m.key}`}
            onClick={() => setMode(m.key)}
            className={`rounded-lg px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition ${
              mode === m.key ? "bg-cyan-500/15 text-cyan-300 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.45)]" : "border border-slate-800 text-slate-500 hover:text-slate-300"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
          <Activity className="h-3.5 w-3.5 text-cyan-500" />
          <select data-testid="pair-game-filter" value={gameF} onChange={(e) => setGameF(e.target.value)} className="bg-transparent font-mono text-[10px] uppercase text-slate-300 focus:outline-none">
            <option value="ALL">All Games</option>
            {data.games.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
          <Users className="h-3.5 w-3.5 text-cyan-500" />
          <select data-testid="pair-team-filter" value={teamF} onChange={(e) => setTeamF(e.target.value)} className="bg-transparent font-mono text-[10px] uppercase text-slate-300 focus:outline-none">
            <option value="ALL">All Teams</option>
            {data.teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
          <Search className="h-3.5 w-3.5 text-cyan-500" />
          <input
            data-testid="pair-player-search"
            value={playerQ}
            onChange={(e) => setPlayerQ(e.target.value)}
            placeholder="Search player…"
            className="w-32 bg-transparent font-mono text-[10px] text-slate-200 placeholder:text-slate-600 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
          <span className="font-mono text-[9px] uppercase text-slate-500">Sort</span>
          <select data-testid="pair-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent font-mono text-[10px] uppercase text-slate-300 focus:outline-none">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {mode === "all" && data.top10?.length > 0 && <Top10 pairs={data.top10} />}

      {/* 🔥 TOP 10 board (diversified) */}
      <div className="space-y-2" data-testid="pairing-list">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
          🔥 Top {displayed.length} {mode === "all" ? "slate" : MODES.find((m) => m.key === mode)?.label.toLowerCase()} pairs
        </p>
        {displayed.length === 0 ? (
          <p className="py-8 text-center font-mono text-[11px] uppercase tracking-widest text-slate-600">No pairs match these filters</p>
        ) : (
          displayed.map((p) => <PairCard key={`t10-${p.pair_id}`} pair={p} />)
        )}
      </div>

      {/* 📋 QUALIFIED POOL — up to 50 pairs (with contrarian + positional mix) */}
      <div className="space-y-2" data-testid="pairing-pool">
        <button
          onClick={() => setPoolOpen((v) => !v)}
          data-testid="pairing-pool-toggle"
          className="flex w-full items-center justify-between rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-900/80"
        >
          <span>📋 Qualified Pool — {filtered.length} pairs{mode === "all" ? ` of ${data.qualified_pairs}` : ""}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${poolOpen ? "rotate-180" : ""}`} />
        </button>
        {poolOpen && (
          filtered.length === 0 ? (
            <p className="py-8 text-center font-mono text-[11px] uppercase tracking-widest text-slate-600">No pairs in pool for these filters</p>
          ) : (
            filtered.map((p) => (
              <PairCard key={`pool-${p.pair_id}`} pair={p} />
            ))
          )
        )}
      </div>
    </div>
  );
}
